import { useVirtualizer } from '@tanstack/react-virtual';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useMatch } from 'react-router-dom';
import { CreateButton } from '~/components/CreateButton';
import { FloatingActionButton } from '~/components/FloatingActionButton';
import { ListDetailView } from '~/components/ListDetailView';
import { SpaceForm } from '~/components/space/SpaceForm';
import { BasicListItem } from '~/components/ui/BasicListItem';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { Loading } from '~/components/ui/Loading';
import { NavContainer } from '~/components/ui/NavContainer';
import { DisplayState, StatefulView } from '~/components/ui/StatefulView';
import { withModal } from '~/components/withModal';
import {
  Filter,
  ListSpacesQuery,
  Status,
  useListSpacesQuery,
  useLocationsQuery,
} from '~/generated/graphql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useDynamicHeaderMargin } from '~/hooks/useDynamicHeaderMargin';
import { SitesNav } from '~/layouts/nav/SitesNav';
import { IAttribute } from '~/types';
import { SpaceView } from './[id]';

type Space = ListSpacesQuery['spacesConnection']['edges'][0]['node'];

const SpaceFormModal = withModal(SpaceForm);
const SpaceViewModal = withModal(SpaceView);

function displayShortAttributes(attributes: IAttribute[]) {
  return attributes
    .filter(({ name }) => !!name)
    .map(({ name, value }) => `${name}: ${value.join(', ')}`)
    .join('; ');
}

function getListItemProps(space: Space, t: TFunction) {
  return {
    id: space.id,
    linkTo: `/spaces/${space.id}`,
    image: space.image,
    titleText: space.name,
    secondaryText: space.location.__typename
      ? `${space.location.name} (${t(space.location.__typename)})`
      : undefined,
    tertiaryText: space.attributes
      ? displayShortAttributes(space.attributes)
      : undefined,
    // hero: {
    //   number: space.activeSkus || 0,
    //   directive: `Active ${t('item', { count: space.activeSkus })}`,
    // },
    status: space.status,
    active: false, //Boolean(match && match.params.id === space.id.toString()),
  };
}

export const Spaces = () => {
  const isCreatingSpace = Boolean(useMatch('/spaces/new/*'));
  const hasSelectedSpace =
    Boolean(useMatch('/spaces/:spaceId/*')) && !isCreatingSpace;
  const isEditingSpace = Boolean(useMatch('/spaces/:id/edit'));
  const shouldShowOutletActions =
    hasSelectedSpace || isCreatingSpace || isEditingSpace;
  const [cursor, setCursor] = useState<string>();
  const [searchValue, setSearchValue] = useState('');
  const [filtersValue, setFiltersValue] = useState<URLSearchParams>(
    new URLSearchParams({ status: Status.Active })
  );

  const { t } = useTranslation();

  const [{ data: locationsData }] = useLocationsQuery();
  const statusOptions = Object.values(Status).map((value) => ({
    value,
    label: t(`statuses.${value}`),
  }));

  const locationNameOptions = useMemo(() => {
    const names = locationsData?.locations.map(({ name }) => name) ?? [];
    return [...new Set(names)].map((name) => ({ value: name, label: name }));
  }, [locationsData]);

  const apiFilters = useMemo((): Filter[] => {
    const result: Filter[] = [];
    for (const [key, value] of filtersValue.entries()) {
      if (value) {
        result.push({ field: key, values: [value] });
      }
    }
    return result;
  }, [filtersValue]);

  const [result, revalidate] = useListSpacesQuery({
    variables: {
      first: 15,
      after: cursor,
      query: searchValue,
      filters: apiFilters,
    },
  });

  const { data } = result;
  const hasNextPage = Boolean(data?.spacesConnection.pageInfo.hasNextPage);

  const reload = () => revalidate({ requestPolicy: 'network-only' });

  const allRows = useMemo(
    () => result.data?.spacesConnection.edges.map(({ node }) => node) ?? [],
    [result.data]
  );

  const handleFilterChange = useCallback(
    ({
      searchValue: newSearchValue,
      filtersValue: newFiltersValue,
    }: {
      searchValue: string;
      filtersValue: URLSearchParams;
    }) => {
      setSearchValue(newSearchValue);
      setFiltersValue(newFiltersValue);
      setCursor(undefined);
    },
    []
  );

  const fetchNextPage = useCallback(() => {
    if (!result.fetching && hasNextPage) {
      setCursor(data?.spacesConnection.pageInfo.endCursor ?? undefined);
    }
  }, [result.fetching, hasNextPage, data?.spacesConnection.pageInfo.endCursor]);

  const isLoading = result.fetching;
  const areFiltersActive = useMemo(
    () => filtersValue.size > 0 || !!searchValue,
    [filtersValue, searchValue]
  );
  const displayState = useMemo((): DisplayState => {
    if (isLoading && allRows.length === 0) {
      return 'INITIAL_LOADING';
    }
    if (result.error && !isLoading) return 'ERROR';
    if (!isLoading && allRows.length === 0) return 'NO_RESULTS';
    return 'IDLE';
  }, [isLoading, result.error, allRows.length]);

  const isSearchResultEmpty = useMemo(() => {
    return (
      displayState === 'NO_RESULTS' &&
      !!searchValue &&
      !!result.data?.spacesConnection &&
      (result.data.spacesConnection.totalCount ?? 0) > 0
    );
  }, [displayState, searchValue, result.data]);

  const filterConfig = useMemo(() => {
    // There were some inactive filters that were left out at the time of refactoring. They can be seen in @SpaceFilters.tsx
    return [
      {
        name: 'status',
        label: t('status'),
        options: statusOptions,
        type: 'select' as const,
        multiple: true,
      },
      {
        name: 'locationName',
        label: t('location'),
        options: locationNameOptions,
        type: 'select' as const,
        multiple: true,
      },
    ];
  }, [statusOptions, locationNameOptions]);
  const { filterRowRef, dynamicMarginTop } = useDynamicHeaderMargin();

  return (
    // Authorization is intentionally disabled for this view while pricing is being implemented.
    /*  <AuthGate action='manage' subject='feat.items'> */
    <>
      <NavContainer nav={<SitesNav />}>
        <NavContainer.FilterRow ref={filterRowRef}>
          <FilterGroupSearch
            filters={filterConfig}
            value={{ searchValue, filtersValue }}
            onChange={handleFilterChange}
            placeholder={t('Find Spaces...')}
          />
        </NavContainer.FilterRow>
      </NavContainer>
      <ListDetailView
        dynamicMarginTop={dynamicMarginTop ? dynamicMarginTop : undefined}
        count={data?.spacesConnection.totalCount}
        nav={<SitesNav />}
        actions={
          shouldShowOutletActions ? (
            <CreateButton to='new'>Space</CreateButton>
          ) : null
        }
        main={
          <StatefulView
            displayState={displayState}
            activeFilters={areFiltersActive}
            isSearchResultEmpty={isSearchResultEmpty}
            entityName='spaces'
          >
            <NewVirtualList
              allRows={allRows}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
            />
          </StatefulView>
        }
      >
        {/* Ensure a FAB is available on mobile to add a Space with "+ space" label */}
        <Route
          path='*'
          element={
            <FloatingActionButton actions={[{ to: 'new', label: 'Space' }]} />
          }
        />
        <Route
          path='/new/*'
          element={<SpaceFormModal title={t('spaces.new')} reload={reload} />}
        />
        <Route
          path='/:id/edit'
          element={<SpaceFormModal title={t('spaces.edit')} reload={reload} />}
        />
        <Route path='/:spaceId/*' element={<SpaceViewModal />} />
      </ListDetailView>
    </>
    /*  </AuthGate> */
  );
};

function NewVirtualList({
  allRows,
  hasNextPage,
  fetchNextPage,
}: {
  hasNextPage: boolean;
  allRows: Space[];
  fetchNextPage: () => void;
}) {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useBreakpoint();
  // Use a named constant for the estimated row height to avoid magic numbers
  const ESTIMATED_ROW_HEIGHT = isMobile ? 58 : 98;

  const getItemKey = useCallback(
    (index: number) => {
      if (index >= allRows.length) return `loader-${index}`;
      const row = allRows[index];
      return row?.id ?? `row-${index}`;
    },
    [allRows]
  );

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? allRows.length + 1 : allRows.length,
    getScrollElement: () => parentRef.current,
    getItemKey,
    // Fixed-height rows: avoid per-row measuring to reduce layout thrash
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 5,
  });

  return (
    <>
      <div ref={parentRef} className='h-full w-full overflow-auto px-4 pt-1'>
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const item = allRows[virtualRow.index];

            const isLoaderRow = virtualRow.index >= allRows.length;

            return (
              <div
                // Prefer a stable key to minimize remounts during virtualization
                key={getItemKey(virtualRow.index)}
                data-index={virtualRow.index}
                className={'absolute left-0 top-0 w-full'}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {isLoaderRow ? (
                  <LoaderRow onMount={fetchNextPage} />
                ) : (
                  <BasicListItem {...getListItemProps(item, t)} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function LoaderRow({ onMount }: { onMount: () => void }) {
  useEffect(() => {
    onMount();
  }, [onMount]);

  return <Loading spinner />;
}
